import { DateTime } from "luxon";
import { compareVersionStrings } from "./compareVersionStrings";
import type { LegalAcceptanceStatusResponse } from "./legalAcceptanceTypes";
import type { LegalAcceptanceLocalPayload } from "./legalAcceptanceLocal";

function resolvedVersion(
  sessionIsSignedIn: boolean,
  databaseVersion: string | null | undefined,
  localPayload: LegalAcceptanceLocalPayload | null,
  key: "termsVersion" | "privacyVersion"
): string | null {
  if (sessionIsSignedIn && databaseVersion) {
    return databaseVersion;
  }
  return localPayload?.[key] ?? null;
}

function isBehind(accepted: string | null, currentVersion: string): boolean {
  return compareVersionStrings(accepted ?? "0", currentVersion) < 0;
}

function isEffectiveReached(
  effectiveDateIso: string,
  nowUtc: DateTime
): boolean {
  const effective = DateTime.fromISO(effectiveDateIso, { zone: "utc" });
  return nowUtc >= effective;
}

export type LegalAcceptanceUiResolution = {
  mode: "ok" | "soft" | "block";
  dismissKey: string;
  /** No terms or privacy acceptance stored (neither DB nor localStorage). */
  isFirstAcceptancePrompt: boolean;
};

/**
 * soft: new versions published but effective date not reached yet (dismissible per session).
 * block: at least one outstanding document is past its effective date — app must not be used without accepting.
 */
export function resolveLegalAcceptanceUiState(
  published: LegalAcceptanceStatusResponse,
  localPayload: LegalAcceptanceLocalPayload | null,
  sessionIsSignedIn: boolean,
  softDismissToken: string | null,
  nowUtc: DateTime = DateTime.utc()
): LegalAcceptanceUiResolution {
  const dismissKey = `${published.terms.version}|${published.privacy.version}`;
  const termsAccepted = resolvedVersion(
    sessionIsSignedIn,
    published.user?.termsAcceptedVersion,
    localPayload,
    "termsVersion"
  );
  const privacyAccepted = resolvedVersion(
    sessionIsSignedIn,
    published.user?.privacyPolicyAcceptedVersion,
    localPayload,
    "privacyVersion"
  );
  const isFirstAcceptancePrompt = termsAccepted === null && privacyAccepted === null;
  const termsBehind = isBehind(termsAccepted, published.terms.version);
  const privacyBehind = isBehind(privacyAccepted, published.privacy.version);

  if (!termsBehind && !privacyBehind) {
    return { mode: "ok", dismissKey, isFirstAcceptancePrompt: false };
  }
  const termsBlock =
    termsBehind && isEffectiveReached(published.terms.effectiveDateIso, nowUtc);

  const privacyBlock =
    privacyBehind &&
    isEffectiveReached(published.privacy.effectiveDateIso, nowUtc);

  if (termsBlock || privacyBlock) {
    return { mode: "block", dismissKey, isFirstAcceptancePrompt };
  }
  if (softDismissToken === dismissKey) {
    return { mode: "ok", dismissKey, isFirstAcceptancePrompt: false };
  }
  return { mode: "soft", dismissKey, isFirstAcceptancePrompt };
}
